import clamp from 'lodash/clamp';
import groupBy from 'lodash/groupBy';
import truncate from 'lodash/truncate';
import Chords from '../lib/chords';
import config from '../config';
import { getPageLimits } from './pagination-utils';
import { getFilterTree, getSortTree } from './filter-utils';
import { compose, filter } from './fp-utils';
import { getCurriculumImage } from './curriculum-utils';
import { getPathStyleLabel } from './string-utils';
import { Courses, Filter, Lessons } from '../redux-types/root.redux-types';
import { PaginationProps } from '../components/Pagination/Pagination';
import { Breadcrumb } from '../components/Breadcrumbs/Breadcrumbs';
import { BrowseRowContent } from '../components/Browse/types';

// Interface
interface BaseProps {
  baseUrl: string;
  image: string;
  instrument: string;
  sortObj: Filter;
  sortOrder: string;
}

interface BrowseParams {
  genre?: string;
  instrument?: string;
  level?: string;
  offset?: number | string | undefined;
  type?: string;
  skillType?: string;
}

interface GetAPIOptionsProps {
  api: string;
  instrument: string;
  limit: number;
  offset: number;
  sortField: string;
  sortOrder: string;
  style: string;
  type: string;
  level: string | undefined;
}

interface GetAPIOptions {
  instrument: string;
  limit: number;
  offset: number;
  skinny: boolean;
  sortField: string;
  sortOrder: string;
  pickLevel?: string;
  'course-type'?: string;
  'course-style'?: string;
  style?: string;
  type?: string;
}

interface GetBrowseSkillsProps {
  api: string;
  curriculumStyle: string;
  title: string;
  type: string | undefined;
  view: string;
}

interface CurriculumSettings {
  display: {
    lessons?: boolean;
    minutes?: boolean;
    chords?: boolean;
    level?: boolean;
  };
  style: string;
}

interface GetBrowseArtists extends BaseProps {
  api: string;
  curriculumSettings: CurriculumSettings;
  filterObj: Filter;
  limit: number;
  metaType: string;
  sortField: string;
  style: string | undefined;
  title: string;
  type: string;
  view: string;
}

interface GetBrowseSkills extends BaseProps, GetBrowseSkillsProps {
  curriculumSettings: CurriculumSettings;
  filterObj: Filter;
  metaType: string;
  offset: number;
}

interface GetBrowseSongsRiffs extends BaseProps {
  filterObj: Filter;
  offset: number;
  style: string | undefined;
  title: string;
  level: string | undefined;
  type: string;
}

// Functions
export function getBaseProps(
  baseUrl = '/',
  query: Record<string, string> = {},
  instrument = 'guitar',
  type = 'songs',
): BaseProps {
  const currentSort = query.sort ? query.sort : 'asc';
  const image = config.pageBanners[instrument][type] || '';
  return {
    baseUrl,
    image,
    instrument,
    sortObj: getSortTree(baseUrl, currentSort),
    sortOrder: currentSort,
  };
}

export function getBrowseSkillsProps(
  skillType: string | undefined,
  instrument = 'guitar',
): GetBrowseSkillsProps {
  let api = 'lessons';
  let curriculumStyle = skillType === 'chords' ? 'round' : 'square';
  let title = `${instrument} Skill Lessons`;
  let type = skillType;
  let view = 'list';
  if (skillType === 'catalog' || skillType === 'courses') {
    api = 'courses';
    curriculumStyle = 'wide';
    view = 'table';
    title = `${instrument} Skill Courses`;
    type = 'skill';
  }
  return { api, curriculumStyle, title, type, view };
}

export function getGroupedContent(content: BrowseRowContent[]): Record<string, BrowseRowContent[]> {
  return groupBy(content, item => {
    const { title } = item || {};
    const firstChar = (title || '').charAt(0).toUpperCase();
    const alpha = /^[a-zA-Z]*$/;
    if (firstChar.match(alpha)) {
      return firstChar;
    }
    return '#';
  });
}

export const findLessons = compose(
  Object.fromEntries,
  filter(([_k, v]) => v['activity-type'] === 'lesson'),
  Object.entries,
);

export function getCourseDurationFromLessons(lessons: Record<string, any>[]): number {
  if (!lessons || !lessons.length) {
    return 0;
  }
  let total = 0;
  lessons.forEach(lesson => {
    if (lesson && lesson['activity-lesson-video'] && lesson['activity-lesson-video'].duration) {
      total += Number(lesson['activity-lesson-video'].duration);
    }
  });
  return total ? Math.round(total / 60) : 0;
}

function getTableData(
  api: string,
  courses: Courses,
  lessons: Lessons,
  hasSubscription: boolean,
): {
  data: Record<string, any> | undefined;
  urlRoot: string;
} {
  const lessonPrefix = hasSubscription ? '/lesson' : '/lessons';
  const coursePrefix = hasSubscription ? '/course' : '/courses';
  let data;
  let urlRoot = coursePrefix;
  if (api === 'lessons') {
    data = lessons.results;
    urlRoot = lessonPrefix;
  } else if (api === 'lesson') {
    data = lessons;
    urlRoot = '/lesson';
  } else {
    data = courses.results;
  }
  return { data, urlRoot };
}

export function formatTableData(
  api: string,
  courses: Courses,
  lessons: Lessons,
  hasSubscription: boolean,
): BrowseRowContent[] | null {
  const { data, urlRoot } = getTableData(api, courses, lessons, hasSubscription);
  if (!data) return null;
  return Object.keys(data).map(dataKey => {
    const dataObj = data[dataKey];
    const title = truncate(dataObj.title || dataObj['artist-name'], { length: 40 });
    const image = getCurriculumImage(dataObj);
    const items = [];
    if (api === 'artists' && dataObj.courses) {
      dataObj.courses.forEach((courseObj: Record<string, any>) => {
        items.push({
          ...courseObj,
          // eslint-disable-next-line
          // @ts-ignore
          chords: truncate(Chords.getChordNamesInCourse({ lessons: [courseObj] }), { length: 20 }),
          lessons: courseObj.lessons ? courseObj.lessons.length : 1,
          level: courseObj.level || 1,
          minutes: getCourseDurationFromLessons(courseObj.lessons),
          slug: courseObj.slug,
          title: truncate(courseObj.title),
          url: `${urlRoot}/${courseObj.slug}`,
        });
      });
    } else {
      items.push({
        ...dataObj,
        // eslint-disable-next-line
        // @ts-ignore
        chords: truncate(Chords.getChordNamesInCourse(dataObj)),
        lessons: dataObj.lessons ? dataObj.lessons.length : 1,
        level: dataObj.level || 1,
        minutes: getCourseDurationFromLessons(dataObj.lessons || [dataObj]),
        title: dataObj.artists && dataObj.artists[0] ? truncate(dataObj.artists[0].name) : null,
        url: `${urlRoot}/${dataObj.slug}`,
      });
    }
    const url = api === 'lessons' ? `${urlRoot}/${dataObj.slug}` : items[0].url;
    return {
      'access-levels': dataObj['access-levels'],
      image,
      items,
      slug: dataObj.slug || items[0].slug,
      title,
      url,
      objectID: dataObj.id,
    };
  });
}

export function getAPIOptions({
  api,
  instrument,
  limit,
  offset,
  sortField,
  sortOrder,
  style,
  type,
  level,
}: GetAPIOptionsProps): GetAPIOptions {
  const options: GetAPIOptions = {
    instrument,
    limit,
    offset,
    skinny: false,
    sortField,
    sortOrder,
  };
  const parsedType = type === 'riffs' ? 'riff' : type;
  options.sortField = sortOrder === 'start-at' ? 'updated-at' : sortField;
  if (level) {
    if (level === 'made-easy') options.pickLevel = '1';
    if (level === 'making-progress') options.pickLevel = '2';
    if (level === 'challenging') options.pickLevel = '3';
  }
  if (api === 'artists') {
    options['course-type'] = parsedType;
    options['course-style'] = style;
    options.skinny = false;
    return options;
  }
  options.offset = offset * limit;
  options.style = style;
  options.type = parsedType;
  return options;
}

export function getBreadcrumbsFromPath(path: string): Breadcrumb[] | null {
  if (!path) {
    return null;
  }
  const pathArr = path.split('/');
  let pathTotal = '';
  return pathArr
    .filter((pathStr: string) => pathStr !== '')
    .map((pathStr: string) => {
      pathTotal = `${pathTotal}/${pathStr}`;
      return {
        link: pathTotal,
        label: getPathStyleLabel(pathStr),
      };
    });
}

export function getPagination(
  api: string,
  courses: Courses,
  lessons: Lessons,
  url: string,
  suffix = '',
): PaginationProps | null {
  const data: Record<string, any> = api === 'lessons' ? lessons : courses;
  if (data && data.meta && data.meta.count && data.meta.limit < data.meta.total) {
    const { limit, offset, total } = data.meta;
    const pageArr = Array(Math.ceil(total / limit)).fill(limit);
    const pageLength = pageArr.length;
    const current = Math.floor(offset / limit);
    const { startOffset, endOffset } = getPageLimits(current, pageLength);
    const pages = pageArr.map((_p, index) => ({
      current: offset === index,
      display: index <= endOffset && index >= startOffset,
      title: `${index + 1}`,
      url: `${url}/${index + 1}${suffix}`,
    }));
    return {
      current,
      pages,
    };
  }
  return null;
}

export function parseOffset(offset: string | number | undefined): number {
  if (offset && !Object.is(offset, NaN)) {
    return clamp(Number(offset) - 1, 0, 1000);
  }
  return 0;
}

export function parseSongRiffParam(param: string | undefined): string | undefined {
  if (param === 'songs') {
    return 'song';
  }
  if (param === 'riffs') {
    return 'riff';
  }
  return param;
}

export function getBrowseArtists(
  params: BrowseParams = {},
  query: Record<string, any> = {},
  instrument = 'guitar',
  filterArr: string[],
): GetBrowseArtists {
  const { genre, type } = params;
  const api = 'artists';
  const display = { chords: true, level: true };
  const parsedGenre = genre === 'songs' ? 'catalog' : genre;
  const filterObj = getFilterTree(
    { genre: parsedGenre, instrument, type },
    filterArr,
    '',
    '/artists',
  );
  return {
    ...getBaseProps(`/${instrument}/artists/${type}`, query, instrument, type),
    api,
    curriculumSettings: { display, style: 'round' },
    filterObj,
    limit: 5000,
    metaType: api,
    sortField: 'name',
    style: parsedGenre === 'catalog' ? '' : parsedGenre,
    title: `${instrument} Songs by Artist`,
    type: parseSongRiffParam(type) || 'song',
    view: 'table',
  };
}

export function getBrowseSkills(
  params: BrowseParams = {},
  query: Record<string, any> = {},
  instrument = 'guitar',
  filterArr: string[],
): GetBrowseSkills {
  const { offset, skillType } = params;
  const display = { lessons: true, minutes: true };
  const filterObj = getFilterTree({ instrument, courseType: 'skill', skill: skillType }, filterArr);
  const skillProps = getBrowseSkillsProps(skillType, instrument);
  return {
    ...getBaseProps(`/${instrument}/skills/${skillType}`, query, instrument, 'skills'),
    ...skillProps,
    curriculumSettings: { display, style: skillProps.curriculumStyle },
    filterObj,
    metaType: 'skills',
    offset: parseOffset(offset),
  };
}

const formatType = (type = ''): string => {
  if (type.toLowerCase().indexOf('songs') > -1) {
    return 'songs';
  } else if (type.toLowerCase().indexOf('riffs') > -1) {
    return 'riffs';
  }
  return type;
};

export function getBrowseSongsRiffs(
  params: BrowseParams = {},
  query: Record<string, any> = {},
  instrument = 'guitar',
  filterArr: string[],
): GetBrowseSongsRiffs {
  const { offset, genre, level, type } = params;
  const formattedType = formatType(type);
  const filterObj = getFilterTree({ instrument, type: formattedType, level, genre }, filterArr);
  const style = genre !== 'catalog' ? genre : '';
  return {
    ...getBaseProps(
      `/${instrument}/${formattedType}/${level}/${genre || 'catalog'}`,
      query,
      instrument,
      formattedType,
    ),
    filterObj,
    offset: parseOffset(offset),
    style,
    title: `${instrument} ${formattedType}`,
    level,
    type: formattedType === 'riffs' ? 'riff' : 'song',
  };
}
